import { Injectable, inject } from '@angular/core'

import {
  BehaviorSubject,
  Observable,
  first,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs'

import {
  getShadesFormHex,
  getThemeColorFromShades,
} from '@flowx/designer/modules/theme/sections/color'
import { downloadBlob, jsonToBlob, loadLocalFile, parseFileAsJson } from '@flowx/shared/utils'

import { ApiTheme, CreateTheme } from './theme.dictionary'
import { FlxThemesManagementApiService } from './themes-management-api.service'

export const DEFAULT_THEME_COLOR = '#006BD8'
export const DEFAULT_THEME_FONT = 'Open Sans'

@Injectable({
  providedIn: 'root',
})
export class FlxThemesManagementService {
  readonly #themesApi = inject(FlxThemesManagementApiService)
  readonly #themesLoadTrigger$ = new BehaviorSubject<void>(null)

  readonly allThemes$ = this.#themesLoadTrigger$.pipe(
    switchMap(() => this.#themesApi.getThemes()),
    map((themes) => themes.content),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  readonly themes$ = this.allThemes$.pipe(
    map((themes) => themes.filter((theme) => !theme.systemTheme)),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  loadThemes(): void {
    this.#themesLoadTrigger$.next()
  }

  createTheme(themeData: Partial<CreateTheme>): Observable<any> {
    const colorShades = getShadesFormHex(themeData.color || DEFAULT_THEME_COLOR)
    const themeColorShades = getThemeColorFromShades(colorShades)
    return this.#themesApi.createTheme({
      name: themeData.name,
      fontFamily: themeData.fontFamily || DEFAULT_THEME_FONT,
      color: {
        name: 'Primary',
        dark: {},
        light: themeColorShades,
      },
    })
  }

  deleteTheme(flowxUuid: ApiTheme['flowxUuid']): Observable<any> {
    return this.#themesApi.deleteTheme(flowxUuid).pipe(tap(() => this.loadThemes()))
  }

  cloneTheme(flowxUuid: ApiTheme['flowxUuid'], name: ApiTheme['name']): Observable<any> {
    return this.themes$.pipe(
      map((themes) => themes.map(({ name }) => name)),
      first(),
      switchMap((themeNames) =>
        this.#themesApi
          .cloneTheme(flowxUuid, this.#getAvailableCloneName(name, themeNames))
          .pipe(tap(() => this.loadThemes()))
      )
    )
  }

  setDefaultTheme(flowxUuid: ApiTheme['flowxUuid']): Observable<any> {
    return this.#themesApi.setDefaultTheme(flowxUuid).pipe(tap(() => this.loadThemes()))
  }

  exportTheme(
    flowxUuid: ApiTheme['flowxUuid']
  ): Observable<{ entityName: string; themeBlob: Blob }> {
    return this.#themesApi.export(flowxUuid).pipe(
      map((entity) => ({
        entityName: entity.theme.name,
        themeBlob: jsonToBlob(entity),
      })),
      tap(({ entityName, themeBlob }) => {
        downloadBlob(themeBlob, `${entityName}_${new Date().toISOString()}.json`)
      })
    )
  }

  loadThemeFile(): Observable<[FileList, any]> {
    return loadLocalFile({ accept: ['application/json'] }).pipe(
      switchMap((files) => forkJoin([of(files), parseFileAsJson(files[0])]))
    )
  }

  importTheme(
    files: FileList,
    importType?: string,
    name?: ApiTheme['name']
  ): Observable<Record<string, any>> {
    const formData = new FormData()
    formData.append('files', files.item(0))
    return this.#themesApi.import(formData, { importType, name }).pipe(tap(() => this.loadThemes()))
  }

  #getAvailableCloneName(themeName: string, themeNames: string[]): string {
    let cloneName = `Copy of ${themeName}`
    let cloneIndex = 1
    if (themeNames.includes(cloneName)) {
      do {
        cloneName = `Copy of ${themeName} (${cloneIndex})`
        cloneIndex++
      } while (themeNames.includes(cloneName))
    }
    return cloneName
  }
}
